import React, { useCallback, useEffect, useRef, useState } from "react";
import CustomButton from "src/components/CustomButton";
import { GET_COUPON, GET_COUPON_BY_ID } from "src/graphql/query";
import {
  Box,
  Checkbox,
  debounce,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ClearIcon } from "@mui/x-date-pickers";
import ActionMenu from "../Business/ActionMenu";
import { getRole, hasPermissionPage } from "src/components/Common/Utils";
import CouponModal from "./CouponModal";
import CustomTable from "src/components/CustomTable";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteModel from "src/components/Common/DeleteModel";
import { DELETE_COUPON } from "src/graphql/mutations";
import { toast } from "react-toastify";
import Loader from "src/components/Loader";
import ErrorComponent from "src/components/ErrorComponent";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import html2canvas from "html2canvas";
import { QRCodeGenerator } from "./CouponQR";
import logo from "src/assests/images/appLogo.png";
import ReactDOM from "react-dom";

interface CouponDetails {
  _id: string;
  name: string;
  qrCode: string;
  url: string;
}


const CouponDetails: React.FC = () => {
  const qrRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [openDeleteMultipleModel, setOpenDeleteMultipleModel] =
    useState<boolean>(false);
  const [openDeleteAllModel, setOpenDeleteAllModel] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50 });
  const [total, setTotal] = useState(0);
  const [list, setList] = useState<CouponDetails[]>([]);
  const [selectedCoupon, setSelectedCoupon] = useState<CouponDetails | null>(
    null
  );
  const [isEditCoupon, setIsEditCoupon] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [DeleteCoupon] = useMutation(DELETE_COUPON);
  const isSuperAdmin = getRole() === "SuperAdmin";
  const initialCouponObj = {
    _id: "",
    name: "",
    qrCode: "",
    url: "https://www.hisurat.com/apply-coupon",
  };
  const [couponObj, setCouponObj] = useState<any>(initialCouponObj);
  const COLUMNS = [
    {
      headerName: "Name",
      field: "name",
      flex: 1,
      renderCell: ({ row }: any) => (
        <Box
          display="flex"
          alignItems="center"
          sx={{ cursor: "pointer" }}
          onClick={() => {
            if (window) {
              window.open(`/CouponDetails/${row?._id}`, "_blank");
            }
          }}
        >
          <Typography variant="body2" color="textSecondary">
            {row?.name}
          </Typography>
        </Box>
      ),
    },

    {
      headerName: "Slug",
      field: "slug",
      flex: 1,
    },
    {
      headerName: "Quantity",
      field: "quantity",
      flex: 0.5,
    },

    {
      headerName: "Status",
      field: "status",
      flex: 0.5,
    },
    { headerName: "Description", field: "description", width: 250 },
    {
      headerName: "Expire Date",
      field: "expireDate",
      width: 120,
      renderCell: ({ value }: { value: string }) => {
        const formattedDate = new Date(value).toLocaleDateString("en-GB"); // Format: DD/MM/YYYY
        return <span>{formattedDate}</span>;
      },
    },

    {
      headerName: "Actions",
      flex: 1,
      renderCell: ({ row }: { row: CouponDetails }) => (
        <div>
          {hasPermissionPage("CouponDetails", "update") && (
            <Tooltip title="Edit">
              <IconButton
                onClick={() => handleEdit(row)}
                aria-label="edit"
                style={{ marginRight: "8px", color: "#00C5B9" }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
          {hasPermissionPage("CouponDetails", "delete") && (
            <Tooltip title="Delete">
              <IconButton
                onClick={() => handleDelete(row)}
                aria-label="delete"
                style={{ color: "#00C5B9" }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
          {row.url && (
            <Tooltip title="Download QR Code">
              <IconButton
                onClick={() => handleDownload(row.url, row.name)}
                aria-label="download"
                style={{ color: "#00C5B9" }}
              >
                <FileDownloadIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];
  const handleDownload = async (qrCodeUrl: string, couponName: string) => {
    if (!qrCodeUrl) {
      toast.error("QR Code URL is missing");
      return;
    }

    const qrContainer = document.createElement("div");
    document.body.appendChild(qrContainer);

    qrContainer.style.position = "absolute";
    qrContainer.style.left = "-9999px";

    const url = (
      <Box
        sx={{
          p: 2,
          display: "inline-block",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
          textAlign: "center",
        }}
      >
        <QRCodeGenerator url={qrCodeUrl} logoSrc={logo} />
        <div
          style={{
            color: "black",
            fontSize: "20px",
            fontWeight: "bold",
            marginTop: "10px",
          }}
        >
          {couponName}
        </div>
      </Box>
    );
    ReactDOM.render(url, qrContainer);

    setTimeout(async () => {
      const canvas = await html2canvas(qrContainer);
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = `${couponName || "QRCode"}.png`;
      link.click();

      ReactDOM.unmountComponentAtNode(qrContainer);
      document.body.removeChild(qrContainer);
    }, 100);
  };

  const { loading, error, data, refetch } = useQuery(GET_COUPON, {
    variables: {
      page: pagination.page,
      limit: pagination.pageSize,
    },
  });

  useEffect(() => {
    if (data?.getCoupons?.data) {
      const processedData = data?.getCoupons?.data.map((coupon: any) => ({
        ...coupon,
      }));
      setList(processedData);
      setTotal(data?.getCoupons?.count);
    }
  }, [data]);

  const debouncedRefetch = useCallback(
    debounce((term: string) => {
      setSearchTerm(term.length > 0 ? term : "");
      refetch({
        search: term,
        page: pagination.page,
        limit: pagination.pageSize,
      });
    }, 1000),
    [refetch, pagination.page, pagination.pageSize]
  );

  const [getCoupon] = useLazyQuery(GET_COUPON_BY_ID);

  const handleEdit = async (rowData: any) => {
    try {
      if (!rowData?._id) {
        toast.error("Invalid row data");
        console.error("Invalid row data");
        return;
      }

      const res = await getCoupon({
        variables: {
          _id: rowData._id,
        },
      });

      if (res?.data?.getCoupon?.statusCode === 200) {
        const removeTypename = (obj: any): any => {
          if (Array.isArray(obj)) {
            return obj.map(removeTypename);
          } else if (typeof obj === "object" && obj !== null) {
            return Object.keys(obj).reduce((acc, key) => {
              if (key !== "__typename") {
                acc[key] = removeTypename(obj[key]);
              }
              return acc;
            }, {} as any);
          }
          return obj;
        };

        const couponData = removeTypename(res?.data?.getCoupon?.data);
        setCouponObj(couponData);

        // const { __typename, ...couponDetails } = res?.data?.getCampaign.data;
        // setCouponObj(couponDetails);
        setOpenModal(true);
        setIsEditCoupon(true);
      } else {
        toast.error("Failed to fetch Coupon details");
        console.error("Failed to fetch Coupon details");
      }
    } catch (error: any) {
      toast.error(error?.message);
      console.error(
        "An error occurred while fetching Coupon details:",
        error
      );
    }
  };

  const handleDelete = (rowData: CouponDetails) => {
    setSelectedCoupon(rowData);
    setOpenConfirmDialog(true);
  };
  const confirmDelete = async () => {
    try {
      if (selectedCoupon && selectedCoupon._id) {
        const res = await DeleteCoupon({
          variables: {
            _id: selectedCoupon._id,
          },
        });
        if (res?.data?.deleteCoupon?.statusCode === 200) {
          toast.success(
            res?.data?.deleteCoupon?.message ||
            "Coupon deleted successfully!"
          );
          refetch();
        } else {
          throw new Error(res?.data?.deleteCoupon?.message);
        }
      }
    } catch (error: any) {
      toast.error(
        error?.message || "An error occurred while deleting the Coupon."
      );
    } finally {
      setOpenConfirmDialog(false);
      setSelectedCoupon(null);
    }
  };
  const handleCloseModal = () => {
    setCouponObj(initialCouponObj);
    setOpenModal(false);
    setIsEditCoupon(false);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
    debouncedRefetch(value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setPagination((prev) => ({ ...prev, page: 1 }));
    refetch({ search: "", page: 1, limit: pagination.pageSize });
  };

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
    refetch({
      search: searchTerm,
      page: newPage + 1,
      limit: pagination.pageSize,
    });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPagination((prev) => ({ ...prev, pageSize: newPageSize }));
    refetch({
      search: searchTerm,
      page: pagination.page,
      limit: newPageSize,
    });
  };

  if (loading) return <Loader />;
  if (error) return <ErrorComponent />;

  return (
    <>
      <Box p={2}>
        {!openModal && (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
              paddingRight={4}
            >
              <Typography variant="h4" style={{ color: "#00C5B9" }}>
                Coupon QR Code
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <Box width={200}>
                  <TextField
                    label="Search"
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton onClick={handleClearSearch}>
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                {isSuperAdmin && (
                  <ActionMenu
                    isSuperAdmin={isSuperAdmin}
                    setOpenDeleteMultipleModel={setOpenDeleteMultipleModel}
                    setOpenDeleteAllModel={setOpenDeleteAllModel}
                  />
                )}
                {hasPermissionPage("CouponDetails", "add") && (
                  <Box>
                    <CustomButton
                      onClick={() => setOpenModal(true)}
                      variant="contained"
                      className="width: 200px"
                    >
                      Create
                    </CustomButton>
                  </Box>
                )}
                <Box></Box>
              </Box>
            </Box>
            <Box mt={2}>
              <CustomTable
                columns={COLUMNS}
                data={list}
                paginationModel={{
                  page: pagination.page - 1,
                  pageSize: pagination.pageSize,
                }}
                totalCount={total}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </Box>
          </>
        )}
        {openModal && (
          <CouponModal
            handleClose={handleCloseModal}
            refetchCoupon={() => refetch()}
            initialCouponObj={initialCouponObj}
            couponObj={couponObj}
            setCouponObj={setCouponObj}
            isEditCoupon={isEditCoupon}
          />
        )}
        <DeleteModel
          open={openConfirmDialog}
          onClose={() => setOpenConfirmDialog(false)}
          onConfirm={confirmDelete}
          title="Confirm Deletion"
          message="Are you sure you want to delete this Coupon?"
        />
      </Box>
    </>
  );
}

export default CouponDetails;